import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Markdown from 'react-markdown';
import { api } from '../api';
import { BUDDY_EVENTS } from '../buddy';
import '../styles/bidleveling.css';

function fireBuddyEvent(eventType, detail) {
  window.dispatchEvent(new CustomEvent(eventType, { detail }));
}

function formatCurrency(val) {
  if (val === null || val === undefined || val === '') return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(val);
}

// CSI MasterFormat-ish division names for the NetSuite cost code list.
// Used to label division headers in InitScopesModal since the DB only stores leaf codes.
const DIVISION_NAMES = {
  '00': 'Planning and Design',
  '01': 'General Requirements',
  '02': 'Site Construction',
  '03': 'Concrete',
  '04': 'Masonry',
  '05': 'Metals',
  '06': 'Wood and Plastics',
  '07': 'Thermal and Moisture Protection',
  '08': 'Doors and Windows',
  '09': 'Finishes',
  '10': 'Specialties',
  '11': 'Equipment',
  '12': 'Furnishings',
  '13': 'Special Construction',
  '14': 'Conveying Systems',
  '15': 'Mechanical',
  '16': 'Electrical',
  '17': 'Markup and Contingency',
};

// Initialize Scopes Modal — searchable + division-collapsible picker over 691 codes.
// Divisions default collapsed; typing in search auto-expands any division with a match.
function InitScopesModal({ onClose, onSave }) {
  const [allScopes, setAllScopes] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [manualExpanded, setManualExpanded] = useState(new Set());

  useEffect(() => {
    api.getBidLevelingScopes().then(data => {
      setAllScopes(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Group scopes by division (the part of Code before the dash)
  const groups = useMemo(() => {
    const out = {};
    allScopes.forEach(s => {
      const div = (s.Code || '').split('-')[0] || '??';
      if (!out[div]) out[div] = [];
      out[div].push(s);
    });
    return out;
  }, [allScopes]);

  const needle = search.trim().toLowerCase();

  // Filter down within each division; drop divisions with no matches
  const filteredGroups = useMemo(() => {
    if (!needle) return groups;
    const out = {};
    Object.entries(groups).forEach(([div, items]) => {
      const matched = items.filter(s =>
        (s.Code || '').toLowerCase().includes(needle) ||
        (s.Name || '').toLowerCase().includes(needle)
      );
      if (matched.length > 0) out[div] = matched;
    });
    return out;
  }, [groups, needle]);

  // While searching, auto-expand every division that has matches. Otherwise use user's manual choice.
  const expanded = needle
    ? new Set(Object.keys(filteredGroups))
    : manualExpanded;

  function toggleDivision(div) {
    if (needle) return; // collapse/expand is driven by search while typing
    setManualExpanded(prev => {
      const next = new Set(prev);
      if (next.has(div)) next.delete(div); else next.add(div);
      return next;
    });
  }

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function selectAllInDivision(div, items) {
    const ids = items.map(i => i.ScopeCategoryId);
    const allSelected = ids.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  }

  function deselectAll() {
    setSelected(new Set());
  }

  const filteredTotal = Object.values(filteredGroups).reduce((s, arr) => s + arr.length, 0);

  async function handleSave() {
    if (selected.size === 0) return;
    setSaving(true);
    try {
      await onSave([...selected]);
      onClose();
    } catch (err) {
      alert('Error initializing scopes: ' + err.message);
      setSaving(false);
    }
  }

  return (
    <div className="bl-modal-overlay" onClick={onClose}>
      <div className="bl-modal" onClick={e => e.stopPropagation()}>
        <div className="bl-modal-header">
          <h3>Add Cost Codes</h3>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>&times;</button>
        </div>
        <div className="bl-modal-body">
          {loading ? (
            <div className="bl-loading">Loading scope categories...</div>
          ) : (
            <>
              <input
                type="text"
                className="bl-scope-search"
                placeholder="Search cost codes by code or name..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
              />
              <div style={{ marginBottom: '10px', marginTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: '#6b7280' }}>
                <span>
                  {selected.size} selected
                  {needle ? ` · ${filteredTotal} matches` : ` · ${allScopes.length} codes`}
                </span>
                {selected.size > 0 && (
                  <button className="btn btn-secondary btn-sm" onClick={deselectAll}>Deselect All</button>
                )}
              </div>
              {Object.keys(filteredGroups).length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>No cost codes match "{search}"</div>
              ) : (
                Object.entries(filteredGroups).sort(([a], [b]) => a.localeCompare(b)).map(([div, items]) => {
                  const isOpen = expanded.has(div);
                  const ids = items.map(i => i.ScopeCategoryId);
                  const selectedInDiv = ids.filter(id => selected.has(id)).length;
                  const divisionName = DIVISION_NAMES[div] || `Division ${div}`;
                  return (
                    <div key={div} className="bl-scope-division">
                      <div
                        className={`bl-scope-division-header ${isOpen ? 'open' : ''}`}
                        onClick={() => toggleDivision(div)}
                        role="button"
                      >
                        <span className="bl-scope-division-chevron">{isOpen ? '▾' : '▸'}</span>
                        <span className="bl-scope-division-code">{div}</span>
                        <span className="bl-scope-division-name">{divisionName}</span>
                        <span className="bl-scope-division-count">
                          {selectedInDiv > 0 ? `${selectedInDiv} / ${items.length}` : items.length}
                        </span>
                        {isOpen && (
                          <button
                            type="button"
                            className="bl-scope-division-all"
                            onClick={(e) => { e.stopPropagation(); selectAllInDivision(div, items); }}
                          >
                            {selectedInDiv === items.length ? 'Clear' : 'All'}
                          </button>
                        )}
                      </div>
                      {isOpen && (
                        <div className="bl-scope-division-items">
                          {items.map(scope => (
                            <label key={scope.ScopeCategoryId} className="bl-scope-checkbox">
                              <input
                                type="checkbox"
                                checked={selected.has(scope.ScopeCategoryId)}
                                onChange={() => toggle(scope.ScopeCategoryId)}
                              />
                              <span className="bl-scope-checkbox-code">{scope.Code}</span>
                              <span>{scope.Name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </>
          )}
        </div>
        <div className="bl-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || selected.size === 0}>
            {saving ? 'Saving...' : `Add ${selected.size} Cost Code${selected.size !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Split-pane cell — used in the PDF viewer's left panel so the estimator can edit cells
// while the relevant passage is shown on the right. Each state button auto-saves.
function SplitCell({ bid, lineItem, cellData, onSave, onFind, onJumpToPage, finding, locked }) {
  const [draftText, setDraftText] = useState(cellData?.text || '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraftText(cellData?.text || ''); }, [cellData?.text]);

  const state = cellData?.state || null;
  const stateClasses = { included: 'bl-cell-state-included', excluded: 'bl-cell-state-excluded', clarify: 'bl-cell-state-clarify' };

  async function handleStateClick(newState) {
    if (locked) return;
    setSaving(true);
    try { await onSave({ state: newState === state ? null : newState }); } finally { setSaving(false); }
  }

  async function handleTextBlur() {
    if (locked) return;
    if (draftText === (cellData?.text || '')) return;
    setSaving(true);
    try { await onSave({ text: draftText }); } finally { setSaving(false); }
  }

  return (
    <div className={`bl-split-cell ${locked ? 'bl-split-cell-locked' : ''}`}>
      <div className="bl-split-cell-title">{lineItem.Description}</div>
      <div className="bl-cell-state-row">
        {['included', 'excluded', 'clarify'].map(s => (
          <button key={s} type="button"
            className={`bl-cell-state-btn ${stateClasses[s]} ${state === s ? 'active' : ''}`}
            onClick={() => handleStateClick(s)}
            disabled={saving || locked}
          >
            {s === 'included' ? '✓ Included' : s === 'excluded' ? '✗ Excluded' : '? Clarify'}
          </button>
        ))}
      </div>
      <textarea
        value={draftText}
        onChange={e => setDraftText(e.target.value)}
        onBlur={handleTextBlur}
        placeholder={locked ? '(locked)' : 'Notes…'}
        rows={2}
        className="bl-split-cell-notes"
        readOnly={locked}
      />
      <div className="bl-split-cell-actions">
        {bid.SourceDocumentPath && !locked && (
          <button type="button" className="bl-find-btn" disabled={finding} onClick={onFind}>
            {finding ? 'Searching…' : '🔍 Find in PDF'}
          </button>
        )}
        {cellData?.page && (
          <button type="button" className="bl-cell-page-link" onClick={onJumpToPage} title={cellData.quote || `Jump to page ${cellData.page}`}>
            📄 p.{cellData.page}
          </button>
        )}
      </div>
    </div>
  );
}

// Add Sub Bid Form
function AddBidForm({ projectId, scopeId, onSaved, onCancel }) {
  const [subName, setSubName] = useState('');
  const [baseAmount, setBaseAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!subName.trim() || !baseAmount) return;
    setSaving(true);
    try {
      await api.addSubBid(projectId, scopeId, {
        SubName: subName.trim(),
        BaseAmount: parseFloat(baseAmount),
        Notes: notes.trim() || null,
      });
      onSaved();
    } catch (err) {
      alert('Error adding bid: ' + err.message);
      setSaving(false);
    }
  }

  return (
    <div className="bl-add-bid-form">
      <h4>Add Sub Bid</h4>
      <form onSubmit={handleSubmit}>
        <div className="bl-form-row">
          <label>Subcontractor Name</label>
          <input
            type="text"
            value={subName}
            onChange={e => setSubName(e.target.value)}
            placeholder="e.g. ABC Electric"
            autoFocus
          />
        </div>
        <div className="bl-form-row">
          <label>Base Amount</label>
          <input
            type="number"
            value={baseAmount}
            onChange={e => setBaseAmount(e.target.value)}
            placeholder="0"
            step="0.01"
            min="0"
          />
          {baseAmount && (
            <div className="bl-currency-preview">{formatCurrency(parseFloat(baseAmount))}</div>
          )}
        </div>
        <div className="bl-form-row">
          <label>Notes (optional)</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Any notes about this bid..."
          />
        </div>
        <div className="bl-form-actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !subName.trim() || !baseAmount}>
            {saving ? 'Saving...' : 'Save Bid'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function BidLeveling({ projectId, projectName, bidNumber }) {
  const [scopes, setScopes] = useState([]);
  const [bids, setBids] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [selectedScopeId, setSelectedScopeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInitModal, setShowInitModal] = useState(false);
  const [showAddBid, setShowAddBid] = useState(false);
  const [newLineItem, setNewLineItem] = useState('');
  const [addingLineItem, setAddingLineItem] = useState(false);
  const [bundles, setBundles] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [docBlobUrl, setDocBlobUrl] = useState(null);
  const [cellDraftState, setCellDraftState] = useState(null);
  const [cellDraftText, setCellDraftText] = useState('');
  const [findingPassage, setFindingPassage] = useState(null);
  const [reviewFindings, setReviewFindings] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [showReviewPanel, setShowReviewPanel] = useState(false);

  async function openDocument(docPath, docName, pageNumber = null, quote = null, splitBidId = null) {
    try {
      const res = await fetch(`/api/uploads/${encodeURIComponent(docPath)}`, {
        headers: { 'Accept': 'application/pdf,application/octet-stream,*/*' },
        credentials: 'same-origin',
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setDocBlobUrl(url);
      setViewingDoc({ path: docPath, name: docName, pageNumber, quote, splitBidId });
    } catch (err) {
      alert('Error loading document: ' + err.message);
    }
  }

  // Jump the already-open PDF to a different page/quote without reloading the blob.
  function jumpViewerToPage(pageNumber, quote) {
    setViewingDoc(v => v ? { ...v, pageNumber, quote } : v);
  }

  useEffect(() => {
    return () => { if (docBlobUrl) URL.revokeObjectURL(docBlobUrl); };
  }, [docBlobUrl]); // { bidId, field }
  const [editValue, setEditValue] = useState('');
  const lineItemInputRef = useRef(null);

  async function handleCellSave() {
    if (!editingCell) return;
    try {
      await api.updateSubBidField(editingCell.bidId, editingCell.field, editValue);
      await loadData();
    } catch (err) {
      alert('Error saving: ' + err.message);
    }
    setEditingCell(null);
    setEditValue('');
  }
  const [showBundles, setShowBundles] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  async function handleFileDrop(files, scopeId) {
    if (!files || files.length === 0 || !scopeId) return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('SubName', file.name.replace(/\.[^.]+$/, ''));
      try {
        const res = await fetch(`/api/bid-leveling/${projectId}/scopes/${scopeId}/upload`, {
          method: 'POST',
          credentials: 'same-origin',
          body: formData,
        });
        await res.json();
        fireBuddyEvent(BUDDY_EVENTS.BID_UPLOADED);
      } catch (err) {
        alert('Error uploading: ' + err.message);
      }
    }
    setUploading(false);
    await loadData();
  }

  const loadData = useCallback(async () => {
    try {
      const data = await api.getProjectBidLeveling(projectId);
      const scopeList = Array.isArray(data) ? data : [];
      setScopes(scopeList);
      const allBids = [];
      const allLineItems = [];
      scopeList.forEach(s => {
        (s.bids || []).forEach(b => allBids.push({ ...b, ScopeId: s.ProjectScopeId }));
        (s.lineItems || []).forEach(li => allLineItems.push({ ...li, ScopeId: s.ProjectScopeId }));
      });
      setBids(allBids);
      setLineItems(allLineItems);
    } catch (err) {
      console.error('Error loading bid leveling data:', err);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  async function loadBundles() {
    try {
      const data = await api.getProjectBundles(projectId);
      setBundles(data);
      setShowBundles(true);
    } catch (err) {
      console.error('Error loading bundles:', err);
    }
  }

  async function handleInitScopes(scopeCategoryIds) {
    await api.initProjectScopes(projectId, scopeCategoryIds);
    await loadData();
    const data = await api.getProjectBidLeveling(projectId);
    const scopeList = Array.isArray(data) ? data : [];
    if (scopeList.length > 0 && !selectedScopeId) {
      setSelectedScopeId(scopeList[0].ProjectScopeId);
    }
  }

  async function handleRemoveScope(scope) {
    const bidCount = bids.filter(b => b.ScopeId === scope.ProjectScopeId).length;
    const msg = bidCount > 0
      ? `Remove "${scope.Code} - ${scope.Name}"? This will also delete ${bidCount} sub bid(s) under this scope.`
      : `Remove "${scope.Code} - ${scope.Name}" from this project?`;
    if (!window.confirm(msg)) return;
    try {
      await api.deleteScope(projectId, scope.ProjectScopeId);
      if (selectedScopeId === scope.ProjectScopeId) setSelectedScopeId(null);
      await loadData();
    } catch (err) {
      alert('Error removing scope: ' + err.message);
    }
  }

  async function handleToggleVisibility(scope) {
    try {
      await api.toggleScopeVisibility(projectId, scope.ProjectScopeId, !scope.IsVisible);
      await loadData();
    } catch (err) {
      alert('Error toggling visibility: ' + err.message);
    }
  }

  const [compliance, setCompliance] = useState({});

  async function loadCompliance(bidIds) {
    const results = {};
    for (const bidId of bidIds) {
      try {
        const res = await fetch(`/api/bid-leveling/bids/${bidId}/compliance`, { credentials: 'same-origin' });
        const data = await res.json();
        results[bidId] = {};
        data.forEach(c => { results[bidId][c.CheckType] = c; });
      } catch(e) {}
    }
    setCompliance(results);
  }

  useEffect(() => {
    if (bids.length > 0) {
      loadCompliance(bids.map(b => b.SubBidId));
    }
  }, [bids]);

  async function handleToggleCompliance(bidId, checkType, currentVal) {
    try {
      await fetch(`/api/bid-leveling/bids/${bidId}/compliance`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ checkType, isVerified: !currentVal }),
      });
      await loadCompliance(bids.map(b => b.SubBidId));
      // Fire a celebration event if all three are now verified
      if (!currentVal) {
        const bc = { ...(compliance[bidId] || {}), [checkType]: { IsVerified: true } };
        const allVerified = ['License', 'Insurance', 'Bond'].every(c => bc[c]?.IsVerified);
        if (allVerified) fireBuddyEvent(BUDDY_EVENTS.COMPLIANCE_COMPLETE);
      }
    } catch (err) {
      alert('Error: ' + err.message);
    }
  }

  async function handleToggleRecommend(bidId) {
    try {
      const wasPicked = bids.find(b => b.SubBidId === bidId)?.IsRecommended;
      await api.toggleRecommendBid(bidId);
      await loadData();
      fireBuddyEvent(wasPicked ? BUDDY_EVENTS.BID_UNPICKED : BUDDY_EVENTS.BID_PICKED);
    } catch (err) {
      alert('Error updating recommendation: ' + err.message);
    }
  }

  async function handleRateSub(bidId, rating) {
    try {
      await api.rateSub(bidId, rating);
      await loadData();
      fireBuddyEvent(BUDDY_EVENTS.SUB_RATED, { rating });
    } catch (err) {
      alert('Error rating sub: ' + err.message);
    }
  }

  async function handleToggleLock(bidId, currentlyLocked) {
    const confirmMsg = currentlyLocked
      ? 'Unlock this bid? You\'ll be able to edit cells again.'
      : 'Lock this bid? Cells and the scope checklist will be frozen until you unlock it.';
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.setBidLock(bidId, !currentlyLocked);
      await loadData();
      fireBuddyEvent(currentlyLocked ? BUDDY_EVENTS.BID_UNLOCKED : BUDDY_EVENTS.BID_LOCKED);
    } catch (err) {
      alert('Error toggling lock: ' + err.message);
    }
  }

  async function handleDeleteBid(bidId) {
    if (!window.confirm('Delete this sub bid?')) return;
    try {
      await api.deleteSubBid(bidId);
      await loadData();
      fireBuddyEvent(BUDDY_EVENTS.BID_DELETED);
    } catch (err) {
      alert('Error deleting bid: ' + err.message);
    }
  }

  function titleCase(str) {
    return str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
  }

  async function handleAddLineItem() {
    if (!newLineItem.trim() || !selectedScopeId) return;
    setAddingLineItem(true);
    try {
      await api.addScopeLineItem(projectId, selectedScopeId, titleCase(newLineItem.trim()));
      setNewLineItem('');
      await loadData();
      setTimeout(() => lineItemInputRef.current?.focus(), 50);
    } catch (err) {
      alert('Error adding line item: ' + err.message);
    }
    setAddingLineItem(false);
  }

  const selectedScope = scopes.find(s => s.ProjectScopeId === selectedScopeId);
  const scopeBids = bids.filter(b => b.ScopeId === selectedScopeId);
  const scopeLineItems = lineItems.filter(li => li.ScopeId === selectedScopeId);

  // Cell data model: stored as JSON in bid.Notes.cells[lineItemId] = { state, text, page, quote }
  function getCellData(bid, lineItemId) {
    try {
      const parsed = JSON.parse(bid.Notes || '{}');
      const cells = parsed.cells || {};
      return cells[lineItemId] || null;
    } catch (e) { return null; }
  }

  async function saveCellData(bid, lineItemId, updates) {
    let parsed = {};
    try { parsed = JSON.parse(bid.Notes || '{}'); } catch (e) {}
    if (!parsed.cells) parsed.cells = {};
    const existing = parsed.cells[lineItemId] || {};
    parsed.cells[lineItemId] = { ...existing, ...updates };
    await api.updateSubBidField(bid.SubBidId, 'Notes', JSON.stringify(parsed));
    await loadData();
  }

  async function handleReviewBids() {
    if (!selectedScopeId) return;
    setReviewLoading(true);
    setShowReviewPanel(true);
    try {
      const res = await api.reviewBids(projectId, selectedScopeId);
      setReviewFindings(res.findings || []);
      fireBuddyEvent(BUDDY_EVENTS.REVIEW_RUN);
    } catch (err) {
      alert('Review failed: ' + err.message);
      setShowReviewPanel(false);
    }
    setReviewLoading(false);
  }

  // Determine bid amount extremes for color coding
  const bidAmounts = scopeBids.map(b => b.BaseAmount).filter(a => a != null && a > 0);
  const minAmount = bidAmounts.length > 0 ? Math.min(...bidAmounts) : null;
  const maxAmount = bidAmounts.length > 0 ? Math.max(...bidAmounts) : null;

  function getBidCellClass(amount) {
    if (amount == null || amount <= 0 || bidAmounts.length < 2) return '';
    if (minAmount === maxAmount) return '';
    if (amount === minAmount) return 'bl-cell-lowest';
    if (amount === maxAmount) return 'bl-cell-highest';
    return '';
  }

  function getScopeColor(scope) {
    const scopeBidsForColor = bids.filter(b => b.ScopeId === scope.ProjectScopeId);
    if (scopeBidsForColor.length === 0) return 'gray';
    if (scopeBidsForColor.some(b => b.IsRecommended)) return 'green';
    return 'yellow';
  }

  if (loading) return <div className="bl-loading">Loading bid leveling...</div>;

  return (
    <div className="bid-leveling-page">
      {/* Header */}
      <div className="bl-header">
        <div className="bl-header-left">
          <h3>Bid Leveling</h3>
          <p>{bidNumber} &mdash; {projectName}</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {scopes.length > 0 && (
            <button className="btn btn-secondary btn-sm" onClick={() => {
              if (showBundles) {
                setShowBundles(false);
              } else {
                loadBundles();
              }
            }}>
              {showBundles ? 'Hide' : 'Show'} Bundle Opportunities
            </button>
          )}
          <button className="btn btn-primary" onClick={() => setShowInitModal(true)}>
            {scopes.length === 0 ? 'Add Cost Codes' : '+ Add Cost Codes'}
          </button>
        </div>
      </div>

      {/* Bundle Opportunities */}
      {showBundles && bundles.length > 0 && (
        <div className="bl-bundles">
          <div className="bl-bundle-list">
            {bundles.map((bundle, i) => {
              const bundleTotal = (bundle.scopes || []).reduce((sum, s) => sum + (s.Amount || 0), 0);
              return (
                <div key={i} className="bl-bundle-card">
                  <div className="bl-bundle-sub">{bundle.SubName}</div>
                  {(bundle.scopes || []).map((s, j) => (
                    <div key={j} className="bl-bundle-scope">
                      <span>{s.ScopeCode} - {s.ScopeName}</span>
                      <span>{formatCurrency(s.Amount)}</span>
                    </div>
                  ))}
                  <div className="bl-bundle-total">
                    <span>Bundle Total</span>
                    <span>{formatCurrency(bundleTotal)}</span>
                  </div>
                  {bundle.savings != null && bundle.savings > 0 && (
                    <div className="bl-bundle-savings">
                      Potential savings: {formatCurrency(bundle.savings)} vs. individual lowest
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {showBundles && bundles.length === 0 && (
        <div className="bl-bundles" style={{ padding: '16px', color: '#6b7280', fontSize: '13px' }}>
          No bundle opportunities found. Subs must bid on 2 or more scopes to appear here.
        </div>
      )}

      {/* Main layout */}
      {scopes.length === 0 ? (
        <div className="bl-empty" style={{ minHeight: '300px' }}>
          <div className="bl-empty-icon">&#128203;</div>
          <div>No cost codes added yet. Add the trades that apply to this project.</div>
          <button className="btn btn-primary" onClick={() => setShowInitModal(true)}>
            Add Cost Codes
          </button>
        </div>
      ) : (
        <div className="bl-layout">
          {/* Sidebar */}
          <div className="bl-sidebar">
            <div className="bl-sidebar-header">Scopes ({scopes.length})</div>
            <div className="bl-scope-list">
              {scopes
                .sort((a, b) => (a.Code || '').localeCompare(b.Code || ''))
                .map(scope => {
                  const bidCount = bids.filter(b => b.ScopeId === scope.ProjectScopeId).length;
                  return (
                    <div
                      key={scope.ProjectScopeId}
                      className={`bl-scope-item ${selectedScopeId === scope.ProjectScopeId ? 'active' : ''} ${!scope.IsVisible ? 'hidden-scope' : ''} ${bidCount === 0 && scope.IsVisible !== false ? 'bl-scope-no-bids' : ''}`}
                      onClick={() => {
                        setSelectedScopeId(scope.ProjectScopeId);
                        setShowAddBid(false);
                        document.querySelector('.bl-main')?.scrollTo(0, 0);
                      }}
                    >
                      <div className={`bl-scope-indicator ${getScopeColor(scope)}`} />
                      <div className="bl-scope-info">
                        <div className="bl-scope-code">{scope.Code}</div>
                        <div className="bl-scope-name">{scope.Name}</div>
                      </div>
                      <div className="bl-scope-badges">
                        {bidCount > 0 && <span className="bl-bid-count">{bidCount}</span>}
                        <button
                          className="bl-eye-btn"
                          title={scope.IsVisible !== false ? 'Hide scope' : 'Show scope'}
                          onClick={e => {
                            e.stopPropagation();
                            handleToggleVisibility(scope);
                          }}
                        >
                          {scope.IsVisible !== false ? '👁' : '👁‍🗨'}
                        </button>
                        <button
                          className="bl-delete-bid-btn"
                          title="Remove scope"
                          onClick={e => {
                            e.stopPropagation();
                            handleRemoveScope(scope);
                          }}
                        >
                          &times;
                        </button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Main content */}
          <div
            className={`bl-main ${dragOver ? 'bl-drag-over' : ''}`}
            onDragOver={e => { if (selectedScopeId) { e.preventDefault(); setDragOver(true); } }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => {
              e.preventDefault();
              setDragOver(false);
              if (selectedScopeId && e.dataTransfer.files.length > 0) {
                handleFileDrop(Array.from(e.dataTransfer.files), selectedScopeId);
              }
            }}
          >
            {uploading && (
              <div className="bl-upload-banner">Uploading bid and reading metadata (contact info, amount)… Scope cells stay empty — you'll fill them with AI-assisted Find-in-PDF.</div>
            )}
            {!selectedScope ? (
              <div className="bl-empty">
                <div className="bl-empty-icon">&#8592;</div>
                <div>Select a scope from the list to view bids</div>
              </div>
            ) : (
              <>
                <div className="bl-scope-detail-header">
                  <h3>{selectedScope.Code} &mdash; {selectedScope.Name}</h3>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {scopeBids.length >= 2 && scopeLineItems.length > 0 && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={handleReviewBids}
                        disabled={reviewLoading}
                        title="Second-pass AI review: flags inconsistencies, outliers, and gaps across the entered data"
                      >
                        {reviewLoading ? 'Reviewing…' : '🧐 Review Bids'}
                      </button>
                    )}
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => setShowAddBid(!showAddBid)}
                    >
                      {showAddBid ? 'Cancel' : '+ Add Sub Bid'}
                    </button>
                  </div>
                </div>

                {/* Review findings panel */}
                {showReviewPanel && (
                  <div className="bl-review-panel">
                    <div className="bl-review-panel-header">
                      <strong>AI Review — {reviewFindings ? `${reviewFindings.length} finding${reviewFindings.length === 1 ? '' : 's'}` : 'Analyzing…'}</strong>
                      <button className="btn btn-secondary btn-sm" onClick={() => setShowReviewPanel(false)}>&times;</button>
                    </div>
                    <div className="bl-review-panel-hint">
                      These are flags for you to verify — not decisions. You are the estimator.
                    </div>
                    {reviewLoading && <div style={{ padding: '12px', fontSize: '13px', color: '#666' }}>Analyzing bids…</div>}
                    {!reviewLoading && reviewFindings && reviewFindings.length === 0 && (
                      <div style={{ padding: '12px', fontSize: '13px', color: '#16a34a' }}>✓ No issues flagged — the bids look consistent.</div>
                    )}
                    {!reviewLoading && reviewFindings && reviewFindings.map((f, i) => {
                      const subName = f.subBidId ? scopeBids.find(b => b.SubBidId === f.subBidId)?.SubName : null;
                      return (
                        <div key={i} className={`bl-review-finding bl-review-sev-${f.severity || 'medium'}`}>
                          <div className="bl-review-finding-type">{(f.type || 'issue').replace(/_/g, ' ')}</div>
                          <div className="bl-review-finding-message">{f.message}</div>
                          {(subName || f.scopeItem) && (
                            <div className="bl-review-finding-meta">
                              {subName && <span>{subName}</span>}
                              {subName && f.scopeItem && <span> · </span>}
                              {f.scopeItem && <span>{f.scopeItem}</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add bid form */}
                {showAddBid && (
                  <AddBidForm
                    projectId={projectId}
                    scopeId={selectedScopeId}
                    onSaved={() => { setShowAddBid(false); loadData(); }}
                    onCancel={() => setShowAddBid(false)}
                  />
                )}

                {/* Comparison Grid */}
                {scopeBids.length > 0 ? (
                  <div className="bl-grid-wrapper">
                    <table className="bl-grid">
                      <thead>
                        <tr>
                          <th>Sub Contractor</th>
                          {scopeBids.map(bid => (
                            <th key={bid.SubBidId} className={bid.IsRecommended ? 'recommended-col' : ''} style={!['License', 'Insurance', 'Bond'].every(c => compliance[bid.SubBidId]?.[c]?.IsVerified) ? { borderLeft: '3px solid #dc2626' } : {}}>
                              <div className={`bl-sub-header ${bid.IsLocked ? 'bl-sub-locked' : ''}`}>
                                <div className="bl-sub-header-top">
                                  {editingCell?.bidId === bid.SubBidId && editingCell?.field === 'SubName' ? (
                                    <input value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={handleCellSave} onKeyDown={e => e.key === 'Enter' && handleCellSave()} autoFocus disabled={bid.IsLocked} style={{ fontSize: '13px', fontWeight: 600, width: '100%', padding: '2px 4px', border: '1px solid var(--color-accent)', borderRadius: '3px' }} />
                                  ) : (
                                    <span className="bl-sub-name" style={{ cursor: bid.IsLocked ? 'not-allowed' : 'pointer' }} onClick={() => { if (bid.IsLocked) return; setEditingCell({ bidId: bid.SubBidId, field: 'SubName' }); setEditValue(bid.SubName); }}>{bid.SubName}</span>
                                  )}
                                  <div className="bl-sub-actions">
                                    <button
                                      className={`bl-sub-lock-btn ${bid.IsLocked ? 'locked' : ''}`}
                                      title={bid.IsLocked ? `Locked by ${bid.LockedBy || 'someone'} on ${bid.LockedAt ? new Date(bid.LockedAt).toLocaleDateString() : ''} — click to unlock` : 'Lock this bid — freezes cells until unlocked'}
                                      onClick={() => handleToggleLock(bid.SubBidId, bid.IsLocked)}
                                    >
                                      {bid.IsLocked ? '🔒' : '🔓'}
                                    </button>
                                    {(() => {
                                      const bc = compliance[bid.SubBidId] || {};
                                      const isValidated = bc.License?.IsVerified && bc.Insurance?.IsVerified && bc.Bond?.IsVerified;
                                      return (
                                        <button
                                          className={`bl-pick-btn ${bid.IsRecommended ? 'picked' : ''}`}
                                          title={!isValidated ? 'Cannot pick — License, Insurance & Bond must be verified first' : bid.IsRecommended ? 'Remove pick' : 'Mark as the pick for this scope'}
                                          onClick={() => {
                                            if (!isValidated) { alert('Cannot pick this sub. License, Insurance, and Bond must all be verified first.'); return; }
                                            handleToggleRecommend(bid.SubBidId);
                                          }}
                                          style={!isValidated ? { opacity: 0.3, cursor: 'not-allowed' } : {}}
                                        >
                                          {bid.IsRecommended ? '🏆' : '○'}
                                        </button>
                                      );
                                    })()}
                                    <button
                                      className="bl-delete-bid-btn"
                                      title="Delete this bid"
                                      onClick={() => handleDeleteBid(bid.SubBidId)}
                                    >
                                      &times;
                                    </button>
                                  </div>
                                </div>
                                {/* 5-star rating for the sub-contractor (persists across all bids) */}
                                <div className="bl-sub-rating-row" title={bid.SubRating ? `Rated ${bid.SubRating}/5 — based on prior jobs` : 'Click a star to rate this sub (persists across projects)'}>
                                  {[1,2,3,4,5].map(n => (
                                    <button key={n} type="button" className="bl-rating-star"
                                      onClick={() => handleRateSub(bid.SubBidId, n)}
                                      style={{ color: (bid.SubRating || 0) >= n ? '#f59e0b' : 'var(--color-gray-300)' }}
                                    >{(bid.SubRating || 0) >= n ? '★' : '☆'}</button>
                                  ))}
                                  {bid.SubRating ? (
                                    <span className="bl-rating-label">{bid.SubRating}/5</span>
                                  ) : (
                                    <span className="bl-rating-label bl-rating-unrated">Unrated</span>
                                  )}
                                </div>
                                {bid.IsLocked && (
                                  <div className="bl-sub-locked-badge">
                                    🔒 LOCKED {bid.LockedBy && `by ${bid.LockedBy}`}
                                  </div>
                                )}
                                {/* Contact info */}
                                {(bid.ContactName || bid.Phone || bid.Email || bid.Address) && (
                                  <div style={{ marginTop: '6px', fontSize: '10px', color: '#777', lineHeight: 1.5 }}>
                                    {bid.ContactName && <div>{bid.ContactName}</div>}
                                    {bid.Phone && <div>{bid.Phone}</div>}
                                    {bid.Email && <div><a href={`mailto:${bid.Email}`} style={{ color: 'var(--color-accent)', textDecoration: 'none' }}>{bid.Email}</a></div>}
                                    {bid.Address && <div style={{ fontSize: '9px', color: '#999' }}>{bid.Address}</div>}
                                    {bid.LicenseNumber && <div style={{ fontSize: '9px' }}>Lic# {bid.LicenseNumber}</div>}
                                  </div>
                                )}
                                {/* Compliance checklist — shows AI-extracted reference values; user still checks to verify */}
                                <div style={{ marginTop: '8px', borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: '6px' }}>
                                  {['License', 'Insurance', 'Bond'].map(check => {
                                    const comp = compliance[bid.SubBidId]?.[check];
                                    const verified = comp?.IsVerified;
                                    const aiExtracted = comp?.AiExtracted;
                                    const refValue = comp?.Notes;
                                    return (
                                      <div key={check} style={{ padding: '2px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', cursor: 'pointer', color: verified ? '#16a34a' : '#999' }}>
                                          <input
                                            type="checkbox"
                                            checked={!!verified}
                                            onChange={() => handleToggleCompliance(bid.SubBidId, check, verified)}
                                            style={{ width: '12px', height: '12px', cursor: 'pointer' }}
                                          />
                                          <span style={{ fontWeight: 600 }}>{check}</span>
                                          {verified && comp?.VerifiedBy && (
                                            <span title={`Verified by ${comp.VerifiedBy} on ${new Date(comp.VerifiedAt).toLocaleDateString()}`} style={{ fontSize: '9px', color: '#16a34a' }}>✓</span>
                                          )}
                                          {aiExtracted && !verified && (
                                            <span title="Extracted from the bid PDF by AI — please verify and check the box" className="bl-ai-badge">AI</span>
                                          )}
                                        </label>
                                        {refValue && (
                                          <div className="bl-compliance-ref" title={refValue}>{refValue}</div>
                                        )}
                                      </div>
                                    );
                                  })}
                                  {(() => {
                                    const allVerified = ['License', 'Insurance', 'Bond'].every(c => compliance[bid.SubBidId]?.[c]?.IsVerified);
                                    if (!allVerified) {
                                      return (
                                        <div style={{ marginTop: '4px', padding: '3px 6px', background: '#fef2f2', borderRadius: '3px', fontSize: '10px', fontWeight: 600, color: '#dc2626', textAlign: 'center' }}>
                                          {bid.IsRecommended ? '🚨 RECOMMENDED BUT NOT VALIDATED' : '⚠ NOT VALIDATED'}
                                        </div>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                              </div>
                            </th>
                          ))}
                          <th className="bl-add-sub-col">
                            <button
                              className="bl-add-sub-btn"
                              title="Add another sub bid"
                              onClick={() => setShowAddBid(true)}
                            >
                              +
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Base Amount row */}
                        <tr>
                          <td className="row-label">Base Amount</td>
                          {scopeBids.map(bid => (
                            <td
                              key={bid.SubBidId}
                              className={`${getBidCellClass(bid.BaseAmount)} ${bid.IsRecommended ? 'recommended-col' : ''}`}
                            >
                              {editingCell?.bidId === bid.SubBidId && editingCell?.field === 'BaseAmount' ? (
                                <input type="number" value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={handleCellSave} onKeyDown={e => e.key === 'Enter' && handleCellSave()} autoFocus style={{ fontSize: '14px', fontWeight: 700, width: '100%', padding: '2px 4px', border: '1px solid var(--color-accent)', borderRadius: '3px' }} />
                              ) : (
                                <div style={{ fontSize: '16px', fontWeight: 700, letterSpacing: '-0.3px', cursor: bid.IsLocked ? 'not-allowed' : 'pointer' }} onClick={() => { if (bid.IsLocked) return; setEditingCell({ bidId: bid.SubBidId, field: 'BaseAmount' }); setEditValue(bid.BaseAmount || ''); }}>{formatCurrency(bid.BaseAmount)}</div>
                              )}
                              {bid.BaseAmount && minAmount && maxAmount && minAmount !== maxAmount && bid.BaseAmount === minAmount && (
                                <div style={{ fontSize: '10px', color: '#16a34a', fontWeight: 600, marginTop: '2px' }}>LOWEST</div>
                              )}
                              {bid.BaseAmount && minAmount && maxAmount && minAmount !== maxAmount && bid.BaseAmount === maxAmount && (
                                <div style={{ fontSize: '10px', color: '#dc2626', fontWeight: 600, marginTop: '2px' }}>HIGHEST</div>
                              )}
                              {bid.BaseAmount && minAmount && bid.BaseAmount > minAmount && minAmount > 0 && (
                                <div style={{ fontSize: '10px', color: '#ea580c', fontWeight: 600, marginTop: '2px' }}>+{Math.round(((bid.BaseAmount - minAmount) / minAmount) * 100)}%</div>
                              )}
                            </td>
                          ))}
                          <td className="bl-add-sub-col" />
                        </tr>
                        {/* Document row */}
                        <tr>
                          <td className="row-label">Document</td>
                          {scopeBids.map(bid => (
                            <td key={bid.SubBidId} className={bid.IsRecommended ? 'recommended-col' : ''}>
                              {bid.SourceDocumentPath ? (
                                <button type="button" onClick={e => { e.stopPropagation(); openDocument(bid.SourceDocumentPath, bid.SourceDocumentName, null, null, bid.SubBidId); }} style={{ background: 'none', border: 'none', color: 'var(--color-accent)', textDecoration: 'none', fontSize: '12px', cursor: 'pointer', padding: 0 }}>
                                  📄 {bid.SourceDocumentName || 'View'}
                                </button>
                              ) : <span style={{ color: '#999', fontSize: '12px' }}>No file</span>}
                            </td>
                          ))}
                          <td className="bl-add-sub-col" />
                        </tr>
                        {/* Bid Received Date row */}
                        <tr>
                          <td className="row-label">Bid Received</td>
                          {scopeBids.map(bid => (
                            <td key={bid.SubBidId} className={bid.IsRecommended ? 'recommended-col' : ''}>
                              {editingCell?.bidId === bid.SubBidId && editingCell?.field === 'BidReceivedDate' ? (
                                <input type="date" value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={handleCellSave} onKeyDown={e => e.key === 'Enter' && handleCellSave()} autoFocus style={{ fontSize: '12px', width: '100%', padding: '2px 4px', border: '1px solid var(--color-accent)', borderRadius: '3px' }} />
                              ) : (
                                <span
                                  style={{ fontSize: '12px', cursor: 'pointer', color: bid.BidReceivedDate ? '#333' : '#999' }}
                                  onClick={() => { if (bid.IsLocked) return; setEditingCell({ bidId: bid.SubBidId, field: 'BidReceivedDate' }); setEditValue(bid.BidReceivedDate ? bid.BidReceivedDate.substring(0, 10) : ''); }}
                                >
                                  {bid.BidReceivedDate ? new Date(bid.BidReceivedDate).toLocaleDateString() : 'Not recorded'}
                                </span>
                              )}
                            </td>
                          ))}
                          <td className="bl-add-sub-col" />
                        </tr>
                        {/* Scope checklist comparison rows — AI-assisted (not AI-first) */}
                        {scopeLineItems.length > 0 && scopeLineItems.map(li => (
                          <tr key={`check-${li.LineItemId}`}>
                            <td style={{ fontSize: '12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>{li.Description}</span>
                                <button
                                  className="bl-delete-bid-btn"
                                  title="Delete line item"
                                  style={{ fontSize: '12px', flexShrink: 0 }}
                                  onClick={async () => {
                                    if (!window.confirm(`Delete line item "${li.Description}"?`)) return;
                                    try {
                                      await api.deleteLineItem(projectId, selectedScopeId, li.LineItemId);
                                      await loadData();
                                    } catch (err) {
                                      alert('Error deleting line item: ' + err.message);
                                    }
                                  }}
                                >
                                  &times;
                                </button>
                              </div>
                            </td>
                            {scopeBids.map(bid => {
                              const cell = getCellData(bid, li.LineItemId);
                              const state = cell?.state || null;
                              const text = cell?.text || '';
                              const cellKey = `${bid.SubBidId}-${li.LineItemId}`;
                              const isEditing = editingCell?.bidId === cellKey;
                              const bg = state === 'included' ? '#dcfce7' : state === 'excluded' ? '#fee2e2' : state === 'clarify' ? '#fef3c7' : '#f9fafb';
                              const icon = state === 'included' ? '✓' : state === 'excluded' ? '✗' : state === 'clarify' ? '?' : '○';
                              const stateLabel = state === 'included' ? 'Included' : state === 'excluded' ? 'Excluded' : state === 'clarify' ? 'Needs clarification' : 'Not set';
                              return (
                                <td key={bid.SubBidId} className={bid.IsRecommended ? 'recommended-col' : ''}
                                  style={{ background: bg, fontSize: '11px', verticalAlign: 'top', padding: '6px' }}
                                >
                                  {isEditing ? (
                                    <div>
                                      <div className="bl-cell-state-row">
                                        {['included','excluded','clarify'].map(s => (
                                          <button key={s} type="button"
                                            className={`bl-cell-state-btn bl-cell-state-${s} ${cellDraftState === s ? 'active' : ''}`}
                                            onClick={() => setCellDraftState(s)}
                                          >
                                            {s === 'included' ? '✓ Included' : s === 'excluded' ? '✗ Excluded' : '? Clarify'}
                                          </button>
                                        ))}
                                      </div>
                                      <textarea value={cellDraftText} onChange={e => setCellDraftText(e.target.value)} rows={3}
                                        placeholder="Notes (optional): quantity, pricing detail, conditions…"
                                        autoFocus
                                        style={{ width: '100%', fontSize: '11px', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '3px', marginTop: '6px', resize: 'vertical', fontFamily: 'inherit' }}
                                      />
                                      <div style={{ display: 'flex', gap: '4px', marginTop: '6px', flexWrap: 'wrap' }}>
                                        <button className="btn btn-primary btn-sm" style={{ fontSize: '10px', padding: '3px 10px' }}
                                          onClick={async () => {
                                            await saveCellData(bid, li.LineItemId, { state: cellDraftState, text: cellDraftText });
                                            setEditingCell(null); setCellDraftState(null); setCellDraftText('');
                                          }}
                                          disabled={!cellDraftState && !cellDraftText.trim()}
                                        >Save</button>
                                        <button className="btn btn-secondary btn-sm" style={{ fontSize: '10px', padding: '3px 10px' }}
                                          onClick={() => { setEditingCell(null); setCellDraftState(null); setCellDraftText(''); }}
                                        >Cancel</button>
                                        {bid.SourceDocumentPath && (
                                          <button className="bl-find-btn" type="button"
                                            title="Ask AI to find the passage about this scope item in the bid PDF"
                                            disabled={findingPassage === cellKey}
                                            onClick={async () => {
                                              setFindingPassage(cellKey);
                                              try {
                                                const r = await api.findBidPassage(bid.SubBidId, li.Description);
                                                if (r && r.found) {
                                                  // Open in split-pane mode so the sub's cells are editable next to the PDF
                                                  openDocument(bid.SourceDocumentPath, bid.SourceDocumentName, r.pageNumber, r.quote, bid.SubBidId);
                                                  await saveCellData(bid, li.LineItemId, { page: r.pageNumber, quote: r.quote });
                                                  // Close the inline cell editor — the split-pane sub column takes over
                                                  setEditingCell(null); setCellDraftState(null); setCellDraftText('');
                                                } else {
                                                  alert('AI could not find a passage about "' + li.Description + '" in this bid. You may want to mark it as "Needs clarification" or check the document yourself.');
                                                }
                                              } catch (e) { alert('Find failed: ' + e.message); }
                                              setFindingPassage(null);
                                            }}
                                          >{findingPassage === cellKey ? 'Searching…' : '🔍 Find in PDF'}</button>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <div style={{ cursor: bid.IsLocked ? 'not-allowed' : 'pointer' }}
                                      onClick={() => {
                                        if (bid.IsLocked) return;
                                        setEditingCell({ bidId: cellKey, realBidId: bid.SubBidId, lineItemId: li.LineItemId, field: 'scopeCell' });
                                        setCellDraftState(state);
                                        setCellDraftText(text);
                                      }}
                                    >
                                      <div style={{ fontWeight: 600, fontSize: '12px' }}>
                                        <span style={{ marginRight: '4px' }}>{icon}</span>
                                        {stateLabel}
                                      </div>
                                      {text && (
                                        <div style={{ fontSize: '10px', color: '#555', marginTop: '3px', lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>
                                          <Markdown>{text}</Markdown>
                                        </div>
                                      )}
                                      {cell?.page && bid.SourceDocumentPath && (
                                        <button type="button" className="bl-cell-page-link"
                                          onClick={e => { e.stopPropagation(); openDocument(bid.SourceDocumentPath, bid.SourceDocumentName, cell.page, cell.quote, bid.SubBidId); }}
                                          title={cell.quote || `Jump to page ${cell.page}`}
                                        >📄 p.{cell.page}</button>
                                      )}
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                            <td className="bl-add-sub-col" />
                          </tr>
                        ))}
                        {/* Exclusions row */}
                        {scopeBids.some(b => { try { return JSON.parse(b.Notes || '{}').exclusions?.length > 0; } catch(e) { return false; } }) && (
                        <tr>
                          <td className="row-label" style={{ color: '#dc2626' }}>Exclusions</td>
                          {scopeBids.map(bid => {
                            let exclusions = [];
                            try { exclusions = JSON.parse(bid.Notes || '{}').exclusions || []; } catch(e) {}
                            return (
                              <td key={bid.SubBidId} className={bid.IsRecommended ? 'recommended-col' : ''} style={{ background: exclusions.length > 0 ? '#fee2e2' : 'transparent' }}>
                                {exclusions.length > 0 ? (
                                  <ul style={{ margin: 0, paddingLeft: '14px', fontSize: '11px', color: '#991b1b' }}>
                                    {exclusions.map((ex, i) => <li key={i}>{ex}</li>)}
                                  </ul>
                                ) : <span style={{ fontSize: '11px', color: '#999' }}>None listed</span>}
                              </td>
                            );
                          })}
                          <td className="bl-add-sub-col" />
                        </tr>
                        )}
                        {/* Conditions row */}
                        {scopeBids.some(b => { try { return !!JSON.parse(b.Notes || '{}').conditions; } catch(e) { return false; } }) && (
                        <tr>
                          <td className="row-label">Conditions</td>
                          {scopeBids.map(bid => {
                            let conditions = '';
                            try { conditions = JSON.parse(bid.Notes || '{}').conditions || ''; } catch(e) {}
                            return (
                              <td key={bid.SubBidId} className={bid.IsRecommended ? 'recommended-col' : ''}>
                                <span style={{ fontSize: '11px', color: '#666' }}>{conditions || '-'}</span>
                              </td>
                            );
                          })}
                          <td className="bl-add-sub-col" />
                        </tr>
                        )}
                        {/* Recommended Total summary row */}
                        <tr style={{ borderTop: '2px solid #333', fontWeight: 700 }}>
                          <td className="row-label" style={{ fontWeight: 700 }}>RECOMMENDED TOTAL</td>
                          {scopeBids.map(bid => (
                            <td key={bid.SubBidId} className={bid.IsRecommended ? 'recommended-col' : ''} style={{ textAlign: 'center' }}>
                              {bid.IsRecommended ? (
                                <span style={{ color: '#16a34a', fontWeight: 700, fontSize: '15px' }}>{formatCurrency(bid.BaseAmount)}</span>
                              ) : (
                                <span style={{ color: '#999' }}>-</span>
                              )}
                            </td>
                          ))}
                          <td className="bl-add-sub-col" />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : null}

                {/* Drop zone - always visible when scope selected */}
                <div
                  className={`bl-dropzone ${dragOver ? 'bl-dropzone-active' : ''} ${scopeBids.length === 0 ? 'bl-dropzone-empty' : ''}`}
                  onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                  onDragLeave={e => { e.stopPropagation(); setDragOver(false); }}
                  onDrop={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragOver(false);
                    if (e.dataTransfer.files.length > 0) {
                      handleFileDrop(Array.from(e.dataTransfer.files), selectedScopeId);
                    }
                  }}
                >
                  <div className="bl-dropzone-icon">{scopeBids.length === 0 ? '📄' : '+'}</div>
                  <div className="bl-dropzone-text">
                    {scopeLineItems.length === 0 && scopeBids.length === 0
                      ? 'Step 1: Add your scope checklist items below, then drop sub bids here'
                      : scopeBids.length === 0
                      ? 'Drop a sub bid here — you\'ll review it against your scope checklist'
                      : 'Drop another bid to add to the comparison'}
                  </div>
                  <div className="bl-dropzone-hint">
                    {scopeLineItems.length === 0 ? 'Add line items first so you know what to compare' : 'PDF, Excel, images accepted. After upload, use "Find in PDF" on each cell to jump to the relevant passage.'}
                  </div>
                </div>

                {/* Add scope checklist item */}
                <div className="bl-line-items">
                  <h4>Scope Checklist ({scopeLineItems.length} items)</h4>
                  <div className="bl-add-line-item">
                    <input
                      type="text"
                      placeholder="Add scope item (e.g. Demo Paving Areas)..."
                      value={newLineItem}
                      onChange={e => setNewLineItem(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleAddLineItem()}
                      disabled={addingLineItem}
                      ref={lineItemInputRef}
                    />
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleAddLineItem}
                      disabled={addingLineItem || !newLineItem.trim()}
                    >
                      {addingLineItem ? 'Adding...' : 'Add'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Init Modal */}
      {showInitModal && (
        <InitScopesModal
          onClose={() => setShowInitModal(false)}
          onSave={handleInitScopes}
        />
      )}

      {/* PDF Viewer Modal — single-pane when opened from header/document row, split-pane when opened from a cell (splitBidId set) */}
      {viewingDoc && (() => {
        const splitBid = viewingDoc.splitBidId != null ? bids.find(b => Number(b.SubBidId) === Number(viewingDoc.splitBidId)) : null;
        const splitLineItems = splitBid ? lineItems.filter(li => li.ScopeId === splitBid.ScopeId) : [];
        const closeViewer = () => {
          // Blur any focused input so pending textarea edits fire their onBlur save
          if (document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur();
          }
          setViewingDoc(null);
          if (docBlobUrl) { URL.revokeObjectURL(docBlobUrl); setDocBlobUrl(null); }
        };
        const pdfSrc = docBlobUrl ? docBlobUrl + (viewingDoc.pageNumber ? `#page=${viewingDoc.pageNumber}` : '') : null;

        return (
          <div className="bl-modal-overlay" onClick={closeViewer}>
            <div className={`bl-pdf-modal ${splitBid ? 'bl-pdf-modal-split' : ''}`} onClick={e => e.stopPropagation()}>
              <div className="bl-pdf-modal-header">
                <div className="bl-pdf-modal-title">
                  {splitBid ? (
                    <>
                      <strong>{splitBid.SubName}</strong>
                      <span className="bl-pdf-modal-amount">{formatCurrency(splitBid.BaseAmount)}</span>
                      <span className="bl-pdf-modal-docname">{viewingDoc.name}</span>
                    </>
                  ) : (
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>{viewingDoc.name || 'Document'}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span className="bl-pdf-modal-hint">Tip: Ctrl+F to search the PDF</span>
                  <button className="btn btn-secondary btn-sm" onClick={() => { if (docBlobUrl) window.open(docBlobUrl, '_blank'); }}>Open in New Tab</button>
                  <a href={docBlobUrl || '#'} download={viewingDoc.name} className="btn btn-secondary btn-sm">Download</a>
                  <button className="btn btn-primary btn-sm bl-pdf-modal-done" onClick={closeViewer}>
                    {splitBid ? '✓ Done' : 'Close'}
                  </button>
                </div>
              </div>

              {splitBid ? (
                <div className="bl-pdf-modal-body-split">
                  {/* Left: this sub's scope cells */}
                  <div className="bl-split-left">
                    <div className="bl-split-sub-header">
                      <div>{splitBid.SubName}</div>
                      <div className="bl-split-sub-amount">{formatCurrency(splitBid.BaseAmount)}</div>
                    </div>
                    <div className="bl-split-cells">
                      {splitLineItems.map(li => {
                        const cellData = getCellData(splitBid, li.LineItemId);
                        const findKey = `split-${splitBid.SubBidId}-${li.LineItemId}`;
                        return (
                          <SplitCell
                            key={li.LineItemId}
                            bid={splitBid}
                            lineItem={li}
                            cellData={cellData}
                            locked={!!splitBid.IsLocked}
                            finding={findingPassage === findKey}
                            onSave={(updates) => saveCellData(splitBid, li.LineItemId, updates)}
                            onFind={async () => {
                              setFindingPassage(findKey);
                              try {
                                const r = await api.findBidPassage(splitBid.SubBidId, li.Description);
                                if (r && r.found) {
                                  jumpViewerToPage(r.pageNumber, r.quote);
                                  await saveCellData(splitBid, li.LineItemId, { page: r.pageNumber, quote: r.quote });
                                } else {
                                  alert('AI could not find a passage about "' + li.Description + '" in this bid.');
                                }
                              } catch (e) { alert('Find failed: ' + e.message); }
                              setFindingPassage(null);
                            }}
                            onJumpToPage={() => jumpViewerToPage(cellData.page, cellData.quote)}
                          />
                        );
                      })}
                    </div>
                    <div className="bl-split-footer">
                      <button className="btn btn-primary bl-split-done-btn" onClick={closeViewer}>
                        ✓ Done — Return to Grid
                      </button>
                    </div>
                  </div>

                  {/* Right: PDF */}
                  <div className="bl-split-right">
                    {viewingDoc.quote && (
                      <div className="bl-split-quote-banner">
                        <strong>AI found this passage{viewingDoc.pageNumber ? ` on page ${viewingDoc.pageNumber}` : ''}:</strong>
                        <div style={{ marginTop: '4px', fontStyle: 'italic' }}>"{viewingDoc.quote}"</div>
                      </div>
                    )}
                    {pdfSrc ? (
                      <iframe src={pdfSrc} title="Document Viewer" style={{ flex: 1, border: 'none', width: '100%' }} />
                    ) : (
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#777' }}>Loading document…</div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {viewingDoc.quote && (
                    <div style={{ padding: '10px 20px', background: '#fef9c3', borderBottom: '1px solid #fde68a', fontSize: '12px', color: '#713f12' }}>
                      <strong>AI found this passage{viewingDoc.pageNumber ? ` on page ${viewingDoc.pageNumber}` : ''}:</strong>
                      <div style={{ marginTop: '4px', fontStyle: 'italic' }}>"{viewingDoc.quote}"</div>
                    </div>
                  )}
                  {pdfSrc ? (
                    <iframe src={pdfSrc} title="Document Viewer" style={{ flex: 1, border: 'none', width: '100%' }} />
                  ) : (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#777' }}>Loading document…</div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
